import { Helmet } from 'react-helmet-async';

interface SeoProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
}

const DEFAULT_TITLE =
  'UK Vineyard Guide – Tours, Tastings & Maps | English & Welsh Wine';
const DEFAULT_DESCRIPTION =
  'Discover award-winning vineyards across England & Wales. Interactive maps, vineyard tours, wine tastings, events & local accommodation. Download the UK Vineyard Guide app today.';
const DEFAULT_URL = 'https://www.ukvineyardguide.co.uk';
const DEFAULT_OG_IMAGE = `${DEFAULT_URL}/og-image.jpg`;

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'UK Vineyard Guide',
  url: DEFAULT_URL,
  logo: `${DEFAULT_URL}/favicon.svg`,
  sameAs: [
    'https://www.instagram.com/ukvineyardguide/',
    'https://www.facebook.com/profile.php?id=61564798714929',
    'https://www.linkedin.com/company/uk-vineyard-guide',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'hello@ukvineyardguide.co.uk',
    contactType: 'customer support',
  },
};

const APP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'MobileApplication',
  name: 'UK Vineyard Guide',
  url: DEFAULT_URL,
  description: DEFAULT_DESCRIPTION,
  operatingSystem: 'iOS, Android',
  applicationCategory: 'TravelApplication',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP',
  },
  author: {
    '@type': 'Organization',
    name: 'UK Vineyard Guide',
    url: DEFAULT_URL,
  },
  downloadUrl: [
    'https://apps.apple.com/us/app/uk-vineyard-guide/id6762269588',
    'https://play.google.com/store/apps/details?id=com.avochoc.uk_vineyard',
  ],
};

export default function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl = DEFAULT_URL,
  ogImage = DEFAULT_OG_IMAGE,
}: SeoProps): JSX.Element {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content="UK Vineyard Guide" />

      {/* Apple Smart App Banner */}
      <meta
        name="apple-itunes-app"
        content="app-id=6762269588, app-argument=https://www.ukvineyardguide.co.uk"
      />

      {/* Google Play App Linking */}
      <link
        rel="alternate"
        href="android-app://com.avochoc.uk_vineyard/https/www.ukvineyardguide.co.uk"
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
      <script type="application/ld+json">{JSON.stringify(APP_SCHEMA)}</script>
    </Helmet>
  );
}
